import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'

import { CrmStatusCell } from '@/Containers/CrmSection/Components/Owner/CrmStatusCell'
import { Form, FormField } from '@/Core/UI/Form'
import { EntityBody, EntityLayout, EntityPanel, EntityTabs, FieldCell, FieldGrid, FieldSection } from '@/Ship/Components/EntityCard'

import { ActivityCardHeader } from '../Components/ActivityCardHeader'
import { ActivityHistoryTab } from '../Components/ActivityHistoryTab'
import { ActivityOwnerFields } from '../Components/ActivityOwnerFields'
import { ActivityResultFields } from '../Components/ActivityResultFields'
import { ACTIVITY_FIELD_LABELS as L } from '../Contracts/ActivityFieldLabels'
import { ApplyActivityCreateDefaultsTask } from '../Tasks/ApplyActivityCreateDefaultsTask'

import type { ActivityCrudContextLike, ActivityFormContextLike } from '../Contracts/Activity.types'
import type { ReactNode } from 'react'

interface Props {
    crudContext: ActivityCrudContextLike | undefined
    formContext: ActivityFormContextLike | undefined
}

/** Місце статусу в блоці «Виконання»: його малює не `FormField`, а `CrmStatusCell`. */
const STATUS = 'status'

/**
 * Блоки «Загального» (`SPEC-011` §8.4), кожен відповідає на одне питання.
 * У легасі поля йшли одним стовпцем (`ActivityFormTemplate.jsx:12-116`);
 * склад той самий, порядок — групуванням `EntityCard`.
 *
 * «Контрагент» і «Результат» малюють окремі observer-компоненти: їхня видимість
 * залежить від значень (власник, статус).
 */
const SECTIONS = {
    what: ['type', 'description'],
    when: ['responsible', 'deadline', STATUS],
    service: ['id', 'createdAt', 'createdBy', 'updatedAt', 'updatedBy'],
} as const

/** На всю ширину: довгі тексти. */
const WIDE = new Set<string>(['description'])

/**
 * Значення за замовчуванням форми нової справи — один раз на відкриту форму
 * (`ApplyActivityCreateDefaultsTask`). Окремий компонент без рендера: шаблон
 * значень форми не читає.
 */
const ActivityCreateDefaults = ({ formContext }: { formContext: ActivityFormContextLike }) => {
    useEffect(() => {
        ApplyActivityCreateDefaultsTask(formContext)
    }, [formContext])

    return null
}

/**
 * Картка справи на спільних правилах карток (`EntityLayout`, `SPEC-011`
 * §8.4): один шаблон на перегляд, редагування й створення. Стрічки праворуч
 * немає — коментарів у справи немає ні в легасі, ні в ADR 027.
 *
 * Вкладки — лише в збереженої справи: «Загальне» й «Історія змін» (остання,
 * перемонтовується на кожне відкриття).
 *
 * ⚠️ Значень полів, які редагують, шаблон не читає — лише `uuid`
 * (`SPEC-002` §4.2): інакше ядровий `FormField` перемонтовував би всі поля на
 * кожну літеру. Живі значення показують окремі observer-компоненти
 * (`ActivityCardHeader`, `ActivityOwnerFields`, `ActivityResultFields`,
 * `CrmStatusCell`).
 */
export const ActivityFormTemplate = observer(({ crudContext, formContext }: Props) => {
    const [historyEpoch, setHistoryEpoch] = useState(0)

    const fields = crudContext?.resource?.fields ?? {}
    const uuid = formContext?.values?.uuid
    const isNew = typeof uuid !== 'string' || uuid === ''

    const cell = (name: string): ReactNode => {
        if (name === STATUS) {
            return formContext && !isNew ? (
                <FieldCell key={name}>
                    <CrmStatusCell formContext={formContext} label={L.status} />
                </FieldCell>
            ) : null
        }

        if (!(name in fields)) return null

        return (
            <FieldCell key={name} wide={WIDE.has(name)}>
                <FormField name={name} />
            </FieldCell>
        )
    }

    const grid = (names: readonly string[]) => (
        <Form formContext={formContext}>
            <FieldGrid>{names.map(cell)}</FieldGrid>
        </Form>
    )

    const main = (
        <>
            <FieldSection title="Зміст справи">{grid(SECTIONS.what)}</FieldSection>
            <FieldSection title="Контрагент">{formContext ? <ActivityOwnerFields formContext={formContext} /> : null}</FieldSection>
            <FieldSection title="Виконання">{grid(SECTIONS.when)}</FieldSection>
            <FieldSection title="Результат">
                {formContext ? <ActivityResultFields formContext={formContext} isNew={isNew} /> : null}
            </FieldSection>
            <FieldSection title="Службова інформація" hidden={isNew}>
                {grid(SECTIONS.service)}
            </FieldSection>
        </>
    )

    if (isNew || !formContext) {
        return (
            <EntityLayout>
                {formContext ? <ActivityCreateDefaults formContext={formContext} /> : null}
                <EntityBody main={main} />
            </EntityLayout>
        )
    }

    return (
        <EntityLayout>
            <EntityTabs
                header={<ActivityCardHeader formContext={formContext} />}
                onChange={(key) => {
                    if (key === 'history') setHistoryEpoch((epoch) => epoch + 1)
                }}
                items={[
                    { key: 'general', label: 'Загальне', children: <EntityBody main={main} /> },
                    {
                        key: 'history',
                        label: 'Історія змін',
                        children: (
                            <EntityPanel>
                                <ActivityHistoryTab key={historyEpoch} uuid={uuid} />
                            </EntityPanel>
                        ),
                    },
                ]}
            />
        </EntityLayout>
    )
})
